import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../i18n/LanguageContext";

const HomeCtaBannerSection: React.FC = () => {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const content = (t as any)?.homeCtaBanner || {};
  const title = content.title || "Education put in the right hands.";
  const description =
    content.description ||
    "The right guidance that helps students find the right track, build their learning journey, and excel.";
  const primaryCta = content.primaryCta || "Get in Touch";
  const secondaryCta = content.secondaryCta || "About Us";
  const image = content.image || "/images/quilia-1-aA2Fadydc-unsplash.jpg";

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    if (!("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -15% 0px", threshold: 0.2 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="bg-white pt-16 sm:pt-20 lg:pt-24"
      aria-labelledby="home-cta-banner-title"
    >
      <div className="w-full overflow-hidden">
        <div className="relative min-h-[360px] overflow-hidden sm:min-h-[400px] lg:min-h-[420px]">
          <div
            className={`absolute inset-0 bg-cover bg-center transition-transform duration-[1800ms] ease-out will-change-transform ${
              isVisible ? "scale-100" : "scale-110"
            }`}
            style={{ backgroundImage: `url('${image}')` }}
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-second-color/55" />
          <div className="absolute inset-0 bg-gradient-to-r from-second-color/80 via-second-color/45 to-second-color/70" />

          <div
            className={`relative z-10 flex min-h-[360px] items-center justify-center px-6 py-12 transition-all duration-[900ms] ease-out sm:min-h-[400px] sm:px-10 lg:min-h-[420px] lg:px-16 ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
          >
            <div
              className="flex w-full max-w-[778px] flex-col items-center gap-8 text-center"
            >
              <div className="flex w-full flex-col items-center gap-3">
                <h2
                  id="home-cta-banner-title"
                  className="max-w-[760px] text-balance font-sans text-2xl font-semibold leading-tight text-white sm:text-[32px]"
                >
                  {title}
                </h2>
                <p className="max-w-[680px] text-pretty font-sans text-sm font-normal leading-6 tracking-tight text-slate-100 sm:text-base">
                  {description}
                </p>
              </div>

              <div
                className={`flex w-full flex-col items-center justify-center gap-4 transition-all delay-150 duration-[900ms] ease-out sm:w-auto sm:flex-row ${
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
                }`}
              >
                <Link
                  to="/contact"
                  className="inline-flex min-h-12 w-full items-center justify-center bg-main-color px-6 py-3 text-center font-sans text-base font-medium capitalize leading-6 text-second-color transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-main-color focus:ring-offset-2 focus:ring-offset-second-color sm:w-auto sm:min-w-[176px]"
                >
                  {primaryCta}
                </Link>
                <Link
                  to="/about"
                  className="inline-flex min-h-12 w-full items-center justify-center bg-white px-6 py-3 text-center font-sans text-base font-medium capitalize leading-6 text-teal-900 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-second-color sm:w-auto sm:min-w-[176px]"
                >
                  {secondaryCta}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeCtaBannerSection;
